import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { constants as offerReduxConstants } from '@makemydeal/dr-offer-redux';
import { BALLOON, CASH, FINANCE, LEASE, SpecialPaymentFormula } from '@makemydeal/dr-platform-types';

import * as offerReduxSelectors from './offerRedux';

export type ScopePaymentSummaryFormat = 'print' | 'subtitle';

const FORMAT_OPTIONS = {
    print: { cashSeparator: ' ', omitMissingTerm: false },
    subtitle: { cashSeparator: ', ', omitMissingTerm: true }
} as const;

export const capitalizeOfferType = (offerType: string): string =>
    (offerType || '').charAt(0).toUpperCase() + (offerType || '').slice(1).toLowerCase();

export const isFlexBuyFinance = (scopeState: StateTree, offerType: string): boolean =>
    offerType === FINANCE && offerReduxSelectors.getSpecialPaymentFormula(scopeState) === SpecialPaymentFormula.FordFlexBuy;

export const getScopePaymentSummary = (scopeState: StateTree, offerType: string, format: ScopePaymentSummaryFormat): string => {
    const { cashSeparator, omitMissingTerm } = FORMAT_OPTIONS[format];

    const withTerm = (label: string, months: number | string | undefined): string =>
        omitMissingTerm && !months ? label : `${label} ${months}`;

    switch (offerType) {
        case BALLOON:
        case FINANCE: {
            const apr = offerReduxSelectors.getSellRate(scopeState);
            if (isFlexBuyFinance(scopeState, offerType)) {
                const flexBuyPayments = offerReduxSelectors.getCurrentFlexBuyPayments(scopeState);
                const firstTerm = flexBuyPayments?.firstTermPaymentDetail?.term ?? '';
                const secondTerm = flexBuyPayments?.secondTermPaymentDetail?.term ?? '';
                const firstPayment = formatDollarsAndCents(flexBuyPayments?.firstTermPaymentDetail?.paymentAmount);
                const secondPayment = formatDollarsAndCents(flexBuyPayments?.secondTermPaymentDetail?.paymentAmount);
                return `Flex Buy ${firstTerm}/${secondTerm}, ${firstPayment}/${secondPayment} - ${apr ?? 0}% APR`;
            }
            const months = offerReduxSelectors.getSelectedTermMonths(scopeState);
            const monthlyPayment = offerReduxSelectors.getMonthlyPaymentByOfferType(scopeState, offerType);
            return `${withTerm(capitalizeOfferType(offerType), months)}, ${formatDollarsAndCents(monthlyPayment ?? 0)}/mo - ${
                apr ?? 0
            }% APR`;
        }
        case LEASE: {
            const months = offerReduxSelectors.getSelectedTermMonths(scopeState);
            const rateInfo =
                offerReduxSelectors.getRateType(scopeState) === offerReduxConstants.RATE_TYPE.MoneyFactor
                    ? `${offerReduxSelectors.getMoneyFactor(scopeState)} MF`
                    : `${offerReduxSelectors.getSellRate(scopeState) ?? 0}% APR`;
            const label = withTerm('Lease', months);

            if (offerReduxSelectors.isOnePayLease(scopeState)) {
                const dueAtSigning = offerReduxSelectors.getDueAtSigning(scopeState) ?? 0;
                return `${label}, ${formatDollarsAndCents(dueAtSigning)}/OP - ${rateInfo}`;
            }
            const monthlyPayment = offerReduxSelectors.getMonthlyPaymentByOfferType(scopeState, LEASE);
            return `${label}, ${formatDollarsAndCents(monthlyPayment ?? 0)}/mo - ${rateInfo}`;
        }
        case CASH: {
            const totalAmountDue = offerReduxSelectors.getCashTotalAmountDueAfterCredits(scopeState);
            return `Cash${cashSeparator}${formatDollarsAndCents(totalAmountDue ?? 0)}`;
        }
        default:
            return '';
    }
};
